import { ObjectBuilder, ImgButton, ImgButtonWithText, LayoutUtils, MeshUtils, Rectangle3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { text } from "../../TextResources";
import { MeshName, UiMeshNames } from "../MeshName";
import { XRInputHelper } from "../XRInputHelper";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
export class MessageDialog {
    constructor(menuMesh, menuFunctions) {
        this._meshClickListenerHandler = undefined;
        this._addMeshClickListenerHandler = () => {
            if (this._meshClickListenerHandler === undefined) {
                const meshNames = [];
                for (let meshName of UiMeshNames)
                    if (meshName !== MeshName.messageDialog) // (exclude "self")
                        meshNames.push(meshName);
                this._meshClickListenerHandler = XRInputHelper.getInstance()?.addClickOnMeshListener(meshNames, (isHit) => {
                    if (isHit)
                        MeshUtils.hide(this._mesh2D);
                });
            }
        };
        this._removeMeshClickListenerHandler = () => {
            if (this._meshClickListenerHandler !== undefined) {
                XRInputHelper.getInstance()?.removeClickOnMeshListener(this._meshClickListenerHandler);
                this._meshClickListenerHandler = undefined;
            }
        };
        this._mesh2D = menuMesh;
        this._menuFunctions = menuFunctions;
        // By default, the panel is hidden.
        this.isVisible = false;
        MeshUtils.setVisibility(this._mesh2D, this.isVisible);
        this._createDialog();
    }
    get isVisible() {
        return this._mesh2D.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        if (value)
            this._addMeshClickListenerHandler();
        else
            this._removeMeshClickListenerHandler();
        MeshUtils.setVisibility(this._mesh2D, value);
    }
    openSignInMessage(position, rotation, callback) {
        this._openMessage(position, rotation, text.broadcast_menu_tipMenu_message_signIn_text, text.broadcast_menu_tipMenu_message_signIn_button, LayoutUtils.GetButtonLayout(Env.broadcast.menu.tipMenu.message.actionButton.signIn.imageLayout, this._mesh2D), Env.broadcast.menu.tipMenu.message.actionButton.signIn.textLayout, callback);
    }
    openTopUpMessage(position, rotation, callback) {
        this._openMessage(position, rotation, text.broadcast_menu_tipMenu_message_topUp_text, text.broadcast_menu_tipMenu_message_topUp_button, LayoutUtils.GetButtonLayout(Env.broadcast.menu.tipMenu.message.actionButton.topUp.imageLayout, this._mesh2D), Env.broadcast.menu.tipMenu.message.actionButton.topUp.textLayout, callback);
    }
    openPrivateMessage(position, rotation, callback) {
        this._openMessage(position, rotation, text.broadcast_menu_tipMenu_message_privateShow_text, text.broadcast_menu_tipMenu_message_privateShow_button, LayoutUtils.GetButtonLayout(Env.broadcast.menu.tipMenu.message.actionButton.privateShow.imageLayout, this._mesh2D), Env.broadcast.menu.tipMenu.message.actionButton.privateShow.textLayout, callback);
    }
    openConfirmLeaveMessage(position, rotation, callback) {
        this._openMessage(position, rotation, text.broadcast_menu_tipMenu_message_confirmLeave_text, text.broadcast_menu_tipMenu_message_confirmLeave_button, LayoutUtils.GetButtonLayout(Env.broadcast.menu.tipMenu.message.actionButton.confirmLeave.imageLayout, this._mesh2D), Env.broadcast.menu.tipMenu.message.actionButton.confirmLeave.textLayout, callback);
    }
    hide() {
        this.isVisible = false;
    }
    _openMessage(position, rotation, message, buttonText, buttonLayout, _textLayout, callback) {
        if (position)
            this._mesh2D.mesh.position = position.clone();
        if (rotation)
            this._mesh2D.mesh.rotationQuaternion = rotation.clone();
        this._callback = callback;
        this._textBlock.text = message;
        this._actionButton.changeLayout(buttonLayout);
        this._actionButton.text = buttonText;
        this.isVisible = true;
    }
    _createDialog() {
        const m = Env.broadcast.menu.tipMenu.message;
        const rootContainer = new Rectangle3D("messageDialogRoot");
        rootContainer.cornerRadius = this._mesh2D.m2px(Env.broadcast.menu.cornerRadius);
        rootContainer.background = m.backgroundColor;
        rootContainer.thickness = this._mesh2D.m2px(m.borderThickness);
        rootContainer.color = m.borderColor;
        this._mesh2D.texture.addControl(rootContainer);
        const textContainer = new Container("messageDialogTextContainer");
        textContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        textContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        textContainer.widthInPixels = this._mesh2D.m2px(m.width);
        textContainer.heightInPixels = this._mesh2D.m2px(m.actionButton.top);
        textContainer.paddingLeftInPixels = this._mesh2D.m2px(m.closeButton.width);
        textContainer.paddingRightInPixels = this._mesh2D.m2px(m.closeButton.width);
        rootContainer.addControl(textContainer);
        const t = m.text;
        this._textBlock = ObjectBuilder.createTextBlock3D(this._mesh2D, "messageDialogText", text.broadcast_menu_tipMenu_message_topUp_text, t);
        textContainer.addControl(this._textBlock);
        const c = m.closeButton;
        const closeButton = new ImgButton("messageDialogCloseButton", c.imageLayout);
        closeButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        closeButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        closeButton.widthInPixels = this._mesh2D.m2px(c.width);
        closeButton.heightInPixels = this._mesh2D.m2px(c.height);
        closeButton.paddingLeftInPixels = this._mesh2D.m2px(c.padding.left);
        closeButton.paddingRightInPixels = this._mesh2D.m2px(c.padding.right);
        closeButton.paddingTopInPixels = this._mesh2D.m2px(c.padding.top);
        closeButton.paddingBottomInPixels = this._mesh2D.m2px(c.padding.bottom);
        closeButton.onClickObserver.add(() => {
            this.isVisible = false;
            this._menuFunctions.updateTipMenuButtonLayout();
        });
        rootContainer.addControl(closeButton);
        const btn = m.actionButton;
        this._actionButton = new ImgButtonWithText("messageDialogActionButton", {
            text: text.broadcast_menu_tipMenu_message_topUp_button,
            imageLayout: LayoutUtils.GetButtonLayout(btn.topUp.imageLayout, this._mesh2D),
            textLayout: this._mesh2D.textLayoutM2Px(btn.topUp.textLayout)
        });
        this._actionButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._actionButton.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._actionButton.leftInPixels = this._mesh2D.m2px(btn.left);
        this._actionButton.topInPixels = this._mesh2D.m2px(btn.top);
        this._actionButton.widthInPixels = this._mesh2D.m2px(btn.width);
        this._actionButton.heightInPixels = this._mesh2D.m2px(btn.height);
        this._actionButton.paddingLeftInPixels = this._mesh2D.m2px(btn.padding.left);
        this._actionButton.paddingRightInPixels = this._mesh2D.m2px(btn.padding.right);
        this._actionButton.paddingTopInPixels = this._mesh2D.m2px(btn.padding.top);
        this._actionButton.paddingBottomInPixels = this._mesh2D.m2px(btn.padding.bottom);
        this._actionButton.onClickObserver.add(async () => {
            if (this._callback)
                this._callback();
        });
        rootContainer.addControl(this._actionButton);
    }
}
